<script setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
    modelValue: {
        type: String,
        default: ""
    },
    label: {
        type: String,
        default: ""
    },
    titulo: {
        type: String,
        default: ""
    },
    subtitulo: {
        type: String,
        default: ""
    },
    previewAlt: {
        type: String,
        default: ""
    },
    replaceText: {
        type: String,
        default: ""
    },
    removeText: {
        type: String,
        default: ""
    },
    invalidTypeText: {
        type: String,
        default: ""
    },
    maxSizeText: {
        type: String,
        default: ""
    },
    accept: {
        type: String,
        default: "image/*"
    },
    maxSize: {
        type: Number,
        default: 2 * 1024 * 1024
    },
    required: {
        type: Boolean,
        default: false
    },
    readonly: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(["update:modelValue"]);
const { t } = useI18n();
const fileInput = ref(null);
const errorMessage = ref("");
const isDragging = ref(false);
const requiredRule = value => !!value || t("common.messages.validations.required");

function openFileDialog() {
    if (!props.readonly) fileInput.value?.click();
}

function clearFile() {
    if (props.readonly) return;

    errorMessage.value = "";
    emit("update:modelValue", "");

    if (fileInput.value) {
        fileInput.value.value = "";
    }
}

function handleFile(file) {
    errorMessage.value = "";

    if (!file || props.readonly) return;

    if (props.accept.startsWith("image/") && !file.type.startsWith("image/")) {
        errorMessage.value = props.invalidTypeText;
        return;
    }

    if (file.size > props.maxSize) {
        errorMessage.value = props.maxSizeText || props.subtitulo;
        return;
    }

    const reader = new FileReader();
    reader.onload = () => emit("update:modelValue", reader.result);
    reader.readAsDataURL(file);
}

function handleInputChange(event) {
    handleFile(event.target.files?.[0]);
}

function handleDrop(event) {
    isDragging.value = false;
    handleFile(event.dataTransfer.files?.[0]);
}
</script>

<template>
    <div class="upload-field">
        <div v-if="label" class="trezo-form-group">
            <v-label class="d-block fw-medium text-black" :class="{ 'is-required': required }">
                {{ label }}
            </v-label>
        </div>

        <v-input
            :model-value="modelValue"
            :rules="required ? [requiredRule] : []"
            hide-details="auto"
        >
            <div class="upload-field__content">
                <button
                    type="button"
                    :disabled="readonly"
                    :class="['upload-field__dropzone', {
                        'upload-field__dropzone--active': isDragging,
                        'upload-field__dropzone--readonly': readonly
                    }]"
                    @click="openFileDialog"
                    @dragenter.prevent="!readonly && (isDragging = true)"
                    @dragover.prevent="!readonly && (isDragging = true)"
                    @dragleave.prevent="isDragging = false"
                    @drop.prevent="handleDrop"
                >
                    <span v-if="!modelValue" class="upload-field__empty">
                        <span class="material-symbols-outlined">cloud_upload</span>
                        <strong>{{ titulo }}</strong>
                        <small>{{ subtitulo }}</small>
                    </span>

                    <span v-else class="upload-field__preview">
                        <img :src="modelValue" :alt="previewAlt">
                    </span>
                </button>

                <input
                    ref="fileInput"
                    class="upload-field__input"
                    type="file"
                    :accept="accept"
                    :disabled="readonly"
                    @change="handleInputChange"
                >

                <div v-if="modelValue && !readonly" class="upload-field__actions">
                    <button type="button" @click="openFileDialog">
                        {{ replaceText }}
                    </button>
                    <button type="button" @click="clearFile">
                        {{ removeText }}
                    </button>
                </div>

                <p v-if="errorMessage" class="upload-field__error">
                    {{ errorMessage }}
                </p>
            </div>
        </v-input>
    </div>
</template>

<style lang="scss" scoped>
.upload-field {
    width: 100%;

    &__content {
        display: grid;
        width: 100%;
        gap: 12px;
    }

    &__dropzone {
        display: grid;
        place-items: center;
        width: 100%;
        min-height: 210px;
        padding: 22px;
        border: 1.5px dashed #cfd3e7;
        border-radius: 8px;
        background: rgb(246 247 249);
        cursor: pointer;
        transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;

        &:hover,
        &--active {
            border-color: #605dff;
            background: #fff;
            box-shadow: 0 10px 28px rgba(96, 93, 255, 0.1);
        }

        &--readonly {
            cursor: default;
            opacity: 0.7;
        }
    }

    &__empty {
        display: grid;
        justify-items: center;
        gap: 8px;
        color: var(--bodyColor);
        text-align: center;

        .material-symbols-outlined {
            display: grid;
            place-items: center;
            width: 54px;
            height: 54px;
            border-radius: 8px;
            background: var(--whiteColor);
            color: #605dff;
            font-size: 30px;
            box-shadow: 0 8px 24px rgba(96, 93, 255, 0.1);
        }

        strong {
            max-width: 260px;
            color: var(--blackColor);
            font-size: 15px;
            line-height: 1.4;
        }

        small {
            font-size: 12px;
        }
    }

    &__preview {
        display: grid;
        place-items: center;
        width: 100%;

        img {
            max-width: 220px;
            max-height: 150px;
            object-fit: contain;
        }
    }

    &__input {
        display: none;
    }

    &__actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 10px;

        button {
            padding: 8px 12px;
            border: 1px solid #eceef2;
            border-radius: 8px;
            background: var(--whiteColor);
            color: #605dff;
            font-size: 12px;
            font-weight: 700;
            cursor: pointer;
        }
    }

    &__error {
        margin: 0;
        color: #dc2626;
        font-size: 12px;
        text-align: center;
    }
}

:global(.dark-theme) {
    .upload-field {
        &__dropzone {
            border-color: var(--borderColor);
            background: #15203c;

            &:hover,
            &--active {
                border-color: #8f8dff;
                background: #15203c;
                box-shadow: 0 10px 28px rgba(0, 0, 0, 0.24);
            }
        }

        &__empty {
            .material-symbols-outlined {
                background: #0c1427;
                color: #aeb4ff;
            }
        }

        &__actions button {
            border-color: var(--borderColor);
            background: #15203c;
            color: #aeb4ff;
        }
    }
}
</style>
